const API_URL = process.env.REACT_APP_API_URL || '/api';

export interface HiredEquipmentSubmitRowPayload {
  /**
   * Flat-amount line. Sent with qty 1 / unit `$` / rate = amount; the server
   * re-normalizes and never trusts those three from the client.
   */
  lumpSum?: boolean | null;
  date: string;
  taskId: number;
  vendorId?: number | null;
  vendorName?: string | null;
  resourceLabel?: string | null;
  resourceId?: number | null;
  qty: number;
  unit?: string | null;
  rate: number;
  poNumber?: string | null;
  fuelSurchargeEligible?: boolean | null;
  vehicleType?: string | null;
  vendorMaterial?: boolean | null;
  vendorMaterialNotes?: string | null;
  overtimeEligible?: boolean | null;
  attachment?: {
    filename: string;
    contentType: string;
    size: number;
    url: string;
    key?: string;
  } | null;
  /** HE-F20 optional paper ticket. Not required. */
  ticketNumber?: string | null;
  /**
   * Per-line fuel surcharge $/hr override. Honored only if the caller has
   * fuel:custom_surcharge_override; otherwise the fuels table is used.
   */
  fuelSurchargeOverridePerHour?: number | null;
}

export interface HiredEquipmentSubmitPayload {
  anchor: {
    entryMethod: 'purchase_order' | 'subcontractor' | 'task';
    lockedVendorName?: string | null;
    lockedPoNumber?: string | null;
    serviceAreaId?: number | null;
  };
  rows: HiredEquipmentSubmitRowPayload[];
  internalComment?: string | null;
}

export interface HiredEquipmentSubmitResult {
  formId: string;
  referenceNumber: string;
}

export async function submitHiredEquipment(
  payload: HiredEquipmentSubmitPayload,
): Promise<HiredEquipmentSubmitResult> {
  const response = await fetch(`${API_URL}/hired-equipment/submissions`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let message = 'Failed to submit hired equipment';
    try {
      const body = (await response.json()) as { error?: string; details?: string };
      message = body?.error || body?.details || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return response.json() as Promise<HiredEquipmentSubmitResult>;
}

export async function updateHiredEquipmentLines(
  formId: string,
  payload: {
    rows: HiredEquipmentSubmitRowPayload[];
    resubmit?: boolean;
    internalComment?: string | null;
  },
): Promise<{
  fields: Array<{ id: number; field: string; value: string; path: string; controlType: string; dataType: string; question: string }>;
  status?: string;
}> {
  const response = await fetch(`${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let message = 'Failed to update hired equipment lines';
    try {
      const body = (await response.json()) as { error?: string; details?: string };
      message = body?.error || body?.details || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return response.json();
}

export interface HiredEquipmentFuelSurchargeRow {
  id: number;
  vehicleType: 'ON_ROAD' | 'OFF_ROAD';
  surchargePerHour: number;
  effectiveAt: string;
}

export async function listHiredEquipmentFuelSurcharges(): Promise<HiredEquipmentFuelSurchargeRow[]> {
  const response = await fetch(`${API_URL}/hired-equipment/fuel-surcharges`, {
    credentials: 'include',
  });
  if (!response.ok) {
    let message = 'Failed to load fuel surcharges';
    try {
      const body = (await response.json()) as { error?: string; details?: string };
      message = body?.error || body?.details || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return response.json() as Promise<HiredEquipmentFuelSurchargeRow[]>;
}

export interface HeVendorApprovalEmailPreview {
  formId: string;
  vendorName: string;
  vendorEmail: string | null;
  ticket: string;
  workDateLabel: string;
  serviceArea: string;
  subject: string;
  bodyText: string;
  attachmentFilename: string;
  fromEmail: string;
  fromName: string;
  replyToEmail: string;
  senderDisplayName: string;
  sendgridConfigured: boolean;
  canSend: boolean;
  cannotSendReason: string | null;
}

/** Coordinator edits from the approval email preview. */
export interface HeVendorEmailDraft {
  to: string;
  subject: string;
  bodyText: string;
}

async function parseHiredEquipmentError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string; details?: string };
    return body?.error || body?.details || fallback;
  } catch {
    return fallback;
  }
}

export async function fetchHeVendorApprovalEmailPreview(
  formId: string,
): Promise<HeVendorApprovalEmailPreview> {
  const response = await fetch(
    `${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}/vendor-email`,
    { credentials: 'include' },
  );
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to load vendor email preview'));
  }
  return response.json() as Promise<HeVendorApprovalEmailPreview>;
}

export async function sendHeVendorApprovalEmail(
  formId: string,
  draft?: Partial<HeVendorEmailDraft>,
): Promise<{ sent: boolean; to: string; messageId: string | null }> {
  const response = await fetch(
    `${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}/vendor-email`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft ?? {}),
    },
  );
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to send vendor email'));
  }
  return response.json() as Promise<{ sent: boolean; to: string; messageId: string | null }>;
}

function filenameFromContentDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const unquoted = header.match(/filename=([^;]+)/i);
  return unquoted?.[1]?.trim() || fallback;
}

export async function fetchHeVendorApprovalEmailPdf(
  formId: string,
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(
    `${API_URL}/hired-equipment/submissions/${encodeURIComponent(formId)}/vendor-email/pdf`,
    { credentials: 'include' },
  );
  if (!response.ok) {
    throw new Error(await parseHiredEquipmentError(response, 'Failed to download copy'));
  }
  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
      'hired-equipment.html',
    ),
  };
}
